import type { PollService } from "@busbud/core-types";

import type { PriceMode } from "@app/modules/results/constants/price-mode";
import { PRICE_MODE } from "@app/modules/results/constants/price-mode";
import type { Features } from "@app/types/experiments";

import type { DisplayedPriceConfig } from "../price";
import type { LocalizedPriceParams } from "./v4-migration-helper/normalize-localized-price";
import type { SupportedLocale } from "@config/locales";

import { getLocalizedPrice } from "./get-localized-price";
import { shouldIncludeFees, shouldIncludeTaxes } from "../price";
import { isUDMV4 } from "./v4-migration-helper/is-udm-v4";
import { normalizedLocalizedPrice } from "./v4-migration-helper/normalize-localized-price";

// show the price from the Direction
export type Direction = "outbound" | "return" | "trip";

type PriceOptions = {
  origin_country?: string | undefined;
  user_country: string;
  user_region?: string | undefined;
  locale: SupportedLocale;
  source_name: string;
  price_mode: PriceMode;
  features: Partial<Features>;
};

const retrieveLegsToCalculate = (
  legs:
    | PollService.API.PublicTripV4["prices"]
    | PollService.API.RelatedPublicTripV4["prices"],
  direction: Direction
) => {
  if (direction === "trip") {
    return legs;
  }

  return direction === "outbound" ? [legs[0]] : [legs[1]];
};

const getPrice = (
  localized_price: LocalizedPriceParams,
  {
    origin_country,
    user_country,
    user_region,
    locale,
    source_name,
    price_mode,
    features
  }: PriceOptions
) => {
  const include_fees = shouldIncludeFees({
    origin_country,
    user_country,
    user_region,
    locale,
    sources: [source_name],
    price_mode,
    features
  });
  const include_taxes = shouldIncludeTaxes(
    user_country,
    [source_name],
    price_mode
  );

  return getLocalizedPrice(localized_price, { include_fees, include_taxes });
};

const getCheaperPriceFromLegs = (
  legs:
    | PollService.API.PublicTripV4["prices"]
    | PollService.API.RelatedPublicTripV4["prices"],
  options: PriceOptions
) => {
  let total: number | null = null;

  legs.forEach(leg => {
    options.source_name = leg.source_name;
    const fixed_price = getPrice(leg.fixed_prices, options);
    let breakdown_price: number | null = null;
    leg.group_options.forEach(option => {
      option.passengers.forEach(passenger => {
        passenger.ticket_options.forEach(ticket_option => {
          const breakdown_price_tmp = getPrice(
            ticket_option.price.breakdown,
            options
          );
          if (
            breakdown_price === null ||
            breakdown_price_tmp < breakdown_price
          ) {
            breakdown_price = breakdown_price_tmp;
          }
        });
      });
    });
    if (total === null || fixed_price + breakdown_price! < total) {
      total = fixed_price + breakdown_price!;
    }
  });
  return total;
};

const getUDMV3DisplayPrice = (
  trip: PollService.API.RelatedPublicTrip | PollService.API.PublicTrip,
  type: Direction = "trip",
  config: DisplayedPriceConfig
) => {
  const price_mode = config.price_mode || PRICE_MODE.BASE;
  let common_price_groups: {
    source_name: string;
    localized_price: LocalizedPriceParams;
    segment_ids: string[];
  }[] = [];

  common_price_groups = trip.prices.map(price => {
    return {
      source_name: price.source_name,
      localized_price: normalizedLocalizedPrice(price.prices),
      segment_ids: price.segment_ids
    };
  });

  if (type !== "trip") {
    const leg = trip.trip_legs[type === "outbound" ? 0 : 1];

    common_price_groups = common_price_groups.filter(price => {
      return price.segment_ids.every(id => {
        // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        return leg.segment_ids.includes(id);
      });
    });
  }

  return common_price_groups.reduce(
    (total_price, { localized_price, source_name }) => {
      const include_fees = shouldIncludeFees({
        ...config,
        sources: [source_name],
        price_mode
      });
      const include_taxes = shouldIncludeTaxes(
        config.user_country,
        [source_name],
        price_mode
      );
      return (
        total_price +
        getLocalizedPrice(localized_price, { include_fees, include_taxes })
      );
    },
    0
  );
};

const getUDMV4DisplayPrice = (
  trip: PollService.API.PublicTripV4 | PollService.API.RelatedPublicTripV4,
  type: Direction = "trip",
  config: DisplayedPriceConfig
): number => {
  const legs = retrieveLegsToCalculate(trip.prices, type);
  // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const price = getCheaperPriceFromLegs(legs, {
    ...config,
    source_name: "",
    price_mode: config.price_mode || PRICE_MODE.BASE
  });

  return price ?? 0;
};

export const getDisplayPrice = (
  trip:
    | PollService.API.RelatedPublicTrip
    | PollService.API.PublicTrip
    | PollService.API.PublicTripV4
    | PollService.API.RelatedPublicTripV4,
  type: Direction = "trip",
  config: DisplayedPriceConfig
): number => {
  if (isUDMV4(trip)) {
    return getUDMV4DisplayPrice(trip, type, config);
  } else {
    return getUDMV3DisplayPrice(trip, type, config);
  }
};
